import React from "react";

import {Decimal} from "decimal.js";
import {produce} from "immer";

import * as utils from "@client/utils";

import * as forms from "@client/site/forms";
import {useAppDispatch, useAppSelector} from "@client/store";
import {models, server} from "@reactivated";

let isLoading = false;

export const useWorkoutData = () => {
    const dispatch = useAppDispatch();
    const workouts = useAppSelector((state) => state.search.workouts.data);

    React.useEffect(() => {
        if (workouts == null) {
            if (isLoading) {
                return;
            }
            isLoading = true;
            void (async () => {
                const result = await server.journal.workouts.workouts();

                isLoading = false;
                if (result.type == "success") {
                    dispatch({type: "SET_WORKOUTS", workouts: result.data});
                }
            })();
        }
    }, []);

    return workouts;
};

export function annotateWithExercises<
    T extends
        | server.journal.workouts.Workout.output
        | server.journal.workouts.WorkoutTemplate.output
        | server.journal.workouts.WorkoutSection.output,
>(template: T) {
    if ("groups" in template) {
        return {
            ...template,
            exercises: template.groups.map((g) => g.exercises).flat(),
        };
    }
    return {
        ...template,
        exercises: template.sections
            .map((s) => s.groups)
            .flat()
            .map((g) => g.exercises)
            .flat(),
    };
}

function getSuggestedWeight({
    max,
    set,
    isImperial,
}: {
    max: string | null;
    set: server.journal.workouts.ExerciseSet.output;
    isImperial: boolean;
}) {
    const smallest_plate = new Decimal(isImperial ? "2.5" : "1");

    if (set.weight_percentage == null || max == null) {
        return null;
    }

    const percentage = new Decimal(set.weight_percentage).dividedBy(100);

    return utils
        .roundNumberToNearest(
            new Decimal(max)
                .times(percentage)
                .dividedBy(isImperial ? "0.45359237" : "1"),
            smallest_plate.times(2),
        )
        .toString();
}

function useWorkoutSource(
    workout: Pick<server.journal.workouts.Workout.output, "sections" | "questions">,
    setWorkout?: (
        value: (
            prev: server.journal.workouts.Workout.output,
        ) => server.journal.workouts.Workout.output,
    ) => void,
) {
    const [sections, setSections] = React.useState(workout.sections);
    const [questions, setQuestions] = React.useState(workout.questions);

    if (setWorkout != null) {
        return {
            sections: workout.sections,
            questions: workout.questions,
            setQuestions: (questions: server.journal.workouts.Question.output[]) => {
                setWorkout((prev) => ({
                    ...prev,
                    questions: questions,
                }));
            },
            setSections: (
                value: (
                    prevState: server.journal.workouts.WorkoutSection.output[],
                ) => server.journal.workouts.WorkoutSection.output[],
            ) => {
                setWorkout((prev) => ({
                    ...prev,
                    sections: value(prev.sections),
                }));
            },
        };
    }

    return {
        sections,
        setSections,
        questions,
        setQuestions,
    };
}

export const useWorkout = ({
    workout,
    setWorkout,
    exercise_maxes: maxes,
}: {
    workout: Pick<server.journal.workouts.Workout.output, "sections" | "questions">;
    setWorkout?: (
        value: (
            prev: server.journal.workouts.Workout.output,
        ) => server.journal.workouts.Workout.output,
    ) => void;
    exercise_maxes?: models["journal.workouts.ExerciseMaxes"];
}) => {
    const isImperial = forms.useIsImperial();

    const {
        sections: _sections,
        setSections,
        questions,
        setQuestions,
    } = useWorkoutSource(workout, setWorkout);

    const sections = React.useMemo(() => {
        return _sections.map((section, sectionIndex) => ({
            ...section,
            groups: section.groups.map((group, groupIndex) => ({
                ...group,
                exercises: group.exercises.map((exercise, exerciseIndex) => ({
                    ...exercise,
                    sets: exercise.sets.map((set, setIndex) => ({
                        ...set,
                        suggested_weight: getSuggestedWeight({
                            set,
                            isImperial,
                            max:
                                maxes?.actual.find(
                                    (max) =>
                                        max.exercise_type.uuid == exercise.type.uuid,
                                )?.weight ?? null,
                        }),
                        __update: (
                            set: Partial<server.journal.workouts.ExerciseSet.output>,
                        ) => {
                            setSections((_sections) =>
                                produce(_sections, (draft) => {
                                    draft[sectionIndex].groups[groupIndex].exercises[
                                        exerciseIndex
                                    ].sets[setIndex] = {
                                        ...draft[sectionIndex].groups[groupIndex]
                                            .exercises[exerciseIndex].sets[setIndex],
                                        ...set,
                                    };
                                }),
                            );
                        },
                    })),
                    __update: (
                        exercise: Partial<server.journal.workouts.Exercise.output>,
                    ) => {
                        setSections((_sections) =>
                            produce(_sections, (draft) => {
                                draft[sectionIndex].groups[groupIndex].exercises[
                                    exerciseIndex
                                ] = {
                                    ...draft[sectionIndex].groups[groupIndex].exercises[
                                        exerciseIndex
                                    ],
                                    ...exercise,
                                };
                            }),
                        );
                    },
                    __delete: () => {
                        setSections((_sections) =>
                            produce(_sections, (draft) => {
                                draft[sectionIndex].groups[groupIndex].exercises.splice(
                                    exerciseIndex,
                                    1,
                                );
                            }),
                        );
                    },
                })),
                __update: (
                    group: Partial<server.journal.workouts.ExerciseGroup.output>,
                ) => {
                    setSections((_sections) =>
                        produce(_sections, (draft) => {
                            draft[sectionIndex].groups[groupIndex] = {
                                ...draft[sectionIndex].groups[groupIndex],
                                ...group,
                            };
                        }),
                    );
                },
                __delete: () => {
                    setSections((_sections) =>
                        produce(_sections, (draft) => {
                            draft[sectionIndex].groups.splice(groupIndex, 1);
                        }),
                    );
                },
            })),
            __update: (
                section: Partial<server.journal.workouts.WorkoutSection.output>,
            ) => {
                setSections((_sections) =>
                    produce(_sections, (draft) => {
                        draft[sectionIndex] = {
                            ...draft[sectionIndex],
                            ...section,
                        };
                    }),
                );
            },
        }));
    }, [_sections, maxes]);

    const exercises = sections
        .map((s) => s.groups)
        .flat()
        .map((g) => g.exercises)
        .flat();

    const exerciseTypes = exercises.map((e) => e.type);

    const reset = (
        workout: Pick<server.journal.workouts.Workout.output, "sections" | "questions">,
    ) => {
        setSections(() => workout.sections);
        setQuestions(workout.questions);
    };
    return {
        exercises,
        exerciseTypes,
        sections,
        setSections,
        questions,
        setQuestions,
        reset,
    };
};

export type WorkoutSection = ReturnType<typeof useWorkout>["sections"][number];

export type ExerciseGroup = WorkoutSection["groups"][number];

export type Exercise = ExerciseGroup["exercises"][number];

export type ExerciseSet = Exercise["sets"][number];

type Category = server.journal.workouts.ExerciseType.output["category"];
export type CardioCategory = "CARDIO";
export type StrengthCategory = Exclude<Category, CardioCategory>;

// A narrowed Exercise whose type.category is constrained to a subset, so the
// per-variant editors accept only the categories they actually render. The
// dispatch narrows into these via ts-pattern (plain control flow can't, since
// the discriminant is nested under `type`).
export type ExerciseWithCategory<C extends Category> = Omit<Exercise, "type"> & {
    type: Omit<Exercise["type"], "category"> & {category: C};
};
export type CardioExercise = ExerciseWithCategory<CardioCategory>;
export type StrengthExercise = ExerciseWithCategory<StrengthCategory>;

export type Workout = ReturnType<typeof useWorkout>;

export const WorkoutContext = React.createContext<{
    workout: server.journal.workouts.Workout.output;
    scope: "consumer" | "client" | "manager";
    startRestTimer: (duration: number | null) => void;
} | null>(null);

export const ExerciseContext = React.createContext<{
    exercise: server.journal.workouts.Exercise.output;
} | null>(null);
